import { logger } from '@/lib/logger';
import { useState } from 'react';
import { getAuthError } from '@/lib/auth-error';
import type { ApiResponse, UserSchemaType } from '@asetquhub/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signInWithCustomToken } from 'firebase/auth';
import { useConnection, useDisconnect, useSignMessage } from 'wagmi';
import { auth } from '../lib/firebase';
import axios from 'axios';
import { api, REVIEWER_API_URL_KEY } from '../lib/axios';
import { useFirebaseAuth } from '../contexts/FirebaseAuthContext';
import { createSiweMessage } from '../lib/siwe';
import { clearWagmiStorage } from '../lib/utils';
import * as Sentry from '@sentry/react';

const isUserRejection = (error: Error) =>
  error.message.includes('Signature request rejected') ||
  error.message.includes('canceled the signature request') ||
  error.message.includes('rejected') ||
  error.message.includes('ACTION_REJECTED');

// Get nonce from backend
const getNonce = async (walletAddress: string): Promise<string> => {
  const response = await api.post<ApiResponse<{ nonce: string }>>('/api/auth/nonce', {
    walletAddress,
  });
  const nonce = response.data.data?.nonce;
  if (!nonce) throw new Error('We couldn’t prepare your wallet request. Please try again.');
  return nonce;
};

// Check if user exists
const checkUserExists = async (walletAddress: string): Promise<boolean> => {
  const response = await api.get<ApiResponse<{ exists: boolean }>>(
    `/api/users/check/${walletAddress}`
  );
  if (typeof response.data.data?.exists !== 'boolean') {
    throw new Error('We couldn’t check your account. Please try again.');
  }
  return response.data.data.exists;
};

// Send OTP to email
const sendOtp = async (data: {
  email: string;
  walletAddress: string;
}): Promise<{ message: string; expiresIn: number }> => {
  const response = await api.post<ApiResponse<{ message: string; expiresIn: number }>>(
    '/api/auth/send-otp',
    data
  );
  return response.data.data!;
};

// Verify OTP code
const verifyOtp = async (data: {
  email: string;
  walletAddress: string;
  otpCode: string;
}): Promise<{ verified: boolean; message: string }> => {
  const response = await api.post<ApiResponse<{ verified: boolean; message: string }>>(
    '/api/auth/verify-otp',
    data
  );
  return response.data.data!;
};

// Register with SIWE signature and get Firebase custom token
const register = async (data: {
  walletAddress: string;
  message: string;
  signature: string;
  email: string;
  phone: string;
  otpCode: string;
  referredBy?: string;
}): Promise<{ customToken: string; userId: string }> => {
  const response = await api.post<ApiResponse<{ customToken: string; userId: string }>>(
    '/api/users/register',
    data
  );
  return response.data.data!;
};

// Reviewer wallet login - calls staging backend directly (backend switching)
const reviewerWalletLogin = async (
  walletAddress: string
): Promise<{ customToken: string; userId: string }> => {
  const stagingUrl = import.meta.env.VITE_STAGING_API_URL;
  if (!stagingUrl) throw new Error('Staging URL not configured');

  const response = await axios.post<ApiResponse<{ customToken: string; userId: string }>>(
    `${stagingUrl}/api/auth/reviewer-wallet-login`,
    { walletAddress }
  );
  return response.data.data!;
};

// Login with SIWE signature and get Firebase custom token
const login = async (
  message: string,
  signature: string
): Promise<{ customToken: string; userId: string }> => {
  const response = await api.post<ApiResponse<{ customToken: string; userId: string }>>(
    '/api/auth/login',
    {
      message,
      signature,
    }
  );
  return response.data.data!;
};

export type AuthPhase = 'idle' | 'preparing' | 'signature' | 'submitting' | 'session';

export const useAuth = () => {
  const [authPhase, setAuthPhase] = useState<AuthPhase>('idle');
  const queryClient = useQueryClient();
  const { address, isConnected } = useConnection();
  const { signMessageAsync } = useSignMessage();
  const { mutate: disconnect } = useDisconnect();
  const { signOut: firebaseSignOut } = useFirebaseAuth();

  // Check if user exists
  const checkExists = useQuery({
    queryKey: ['user', 'exists', address],
    queryFn: () => checkUserExists(address!),
    enabled: !!address && isConnected,
    staleTime: 0,
    retry: false,
  });

  // Send OTP mutation
  const sendOtpMutation = useMutation({
    mutationFn: async (userData: { email: string }) => {
      if (!address || !isConnected) {
        throw new Error('Wallet not connected');
      }

      return await sendOtp({
        email: userData.email,
        walletAddress: address,
      });
    },
    onError: error => {
      logger.error('Failed to send OTP', error);
    },
  });

  // Verify OTP mutation
  const verifyOtpMutation = useMutation({
    mutationFn: async (userData: { email: string; otpCode: string }) => {
      if (!address || !isConnected) {
        throw new Error('Wallet not connected');
      }

      return await verifyOtp({
        email: userData.email,
        walletAddress: address,
        otpCode: userData.otpCode,
      });
    },
    onError: error => {
      logger.error('Failed to verify OTP', error);
    },
  });

  // Registration mutation
  const registerMutation = useMutation({
    mutationFn: async (userData: {
      email: string;
      phone: string;
      otpCode: string;
      referredBy?: string;
    }) => {
      if (!address || !isConnected) {
        throw new Error('Wallet not connected');
      }

      setAuthPhase('preparing');
      try {
        // Get nonce from backend
        const nonce = await getNonce(address);

        // Create SIWE message
        const message = createSiweMessage({
          address,
          statement: 'Sign up with your wallet for AsetQuHub',
          nonce,
        });

        const preparedMessage = message.prepareMessage();

        // Sign message with wallet
        setAuthPhase('signature');
        const signature = await signMessageAsync({
          message: preparedMessage,
        });

        // Send to backend for registration and get custom token
        setAuthPhase('submitting');
        const { customToken } = await register({
          walletAddress: address,
          message: preparedMessage,
          signature,
          ...userData,
        });

        // Sign in to Firebase with custom token
        setAuthPhase('session');
        await signInWithCustomToken(auth, customToken);
      } catch (error) {
        throw getAuthError(error, 'We could not create your account. Please try again.');
      } finally {
        setAuthPhase('idle');
      }
    },
    onSuccess: () => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['user', 'exists', address] });
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
    onError: error => {
      logger.error('Failed to register', error);
      if (!isUserRejection(error)) {
        Sentry.captureException(error, {
          tags: { feature: 'auth', action: 'register' },
          extra: { walletAddress: address },
        });
      }
    },
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async () => {
      if (!address || !isConnected) {
        throw new Error('Wallet not connected');
      }

      setAuthPhase('preparing');
      try {
        // Get nonce from backend
        const nonce = await getNonce(address);

        // Create SIWE message
        const message = createSiweMessage({
          address,
          statement: 'Sign in with your wallet to AsetQuHub',
          nonce,
        });

        const preparedMessage = message.prepareMessage();

        // Sign message with wallet
        setAuthPhase('signature');
        const signature = await signMessageAsync({
          message: preparedMessage,
        });

        // Send to backend for verification and get custom token
        setAuthPhase('submitting');
        const { customToken } = await login(preparedMessage, signature);

        // Sign in to Firebase with custom token
        setAuthPhase('session');
        await signInWithCustomToken(auth, customToken);
      } catch (error) {
        throw getAuthError(error, 'We could not sign you in. Please try again.');
      } finally {
        setAuthPhase('idle');
      }
    },
    onSuccess: () => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
    onError: error => {
      logger.error('Failed to authenticate', error);
      if (!isUserRejection(error)) {
        Sentry.captureException(error, {
          tags: { feature: 'auth', action: 'login' },
          extra: { walletAddress: address },
        });
      }
    },
  });

  // Reviewer wallet login - calls staging backend, then switches all API calls to staging
  const reviewerWalletLoginMutation = useMutation({
    mutationFn: async (walletAddress: string) => {
      const { customToken } = await reviewerWalletLogin(walletAddress);
      // Store staging URL so all subsequent API calls go to staging (backend switching)
      const stagingUrl = import.meta.env.VITE_STAGING_API_URL;
      if (stagingUrl) {
        localStorage.setItem(REVIEWER_API_URL_KEY, stagingUrl);
      }
      await signInWithCustomToken(auth, customToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
    onError: error => {
      logger.error('Reviewer wallet login failed', error);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Call backend to revoke Firebase tokens
      try {
        await api.post('/api/auth/logout');
      } catch (error) {
        logger.error('Backend logout failed:', error);
        // Continue with local logout even if backend fails
      }

      // Sign out from Firebase
      await firebaseSignOut();
    },
    onSuccess: () => {
      clearWagmiStorage();
      disconnect();
      // Clear reviewer mode (backend switching) on logout
      localStorage.removeItem(REVIEWER_API_URL_KEY);
      queryClient.clear();
    },
    onError: error => {
      logger.error('Failed to logout', error);
      throw error;
    },
  });

  return {
    authPhase,
    login: loginMutation,
    register: registerMutation,
    logout: logoutMutation,
    sendOtp: sendOtpMutation,
    verifyOtp: verifyOtpMutation,
    checkExists,
    reviewerWalletLogin: reviewerWalletLoginMutation,
  };
};

// Get current user session
export const useSession = () => {
  const { user: firebaseUser, loading: firebaseLoading } = useFirebaseAuth();

  const me = useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      const res = await api.get<ApiResponse<UserSchemaType>>('/api/auth/me');
      return res.data.data;
    },
    // Only fetch when Firebase auth is ready and user is signed in
    enabled: !firebaseLoading && !!firebaseUser,
    staleTime: 60_000,
    retry: false,
  });

  return { me };
};

// Alias for compatibility - returns query object directly for ProtectedRoute
export const useGetUser = () => {
  const { user: firebaseUser, loading: firebaseLoading } = useFirebaseAuth();

  return useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      try {
        const res = await api.get<ApiResponse<UserSchemaType>>('/api/auth/me');
        return res.data.data;
      } catch (error) {
        // If unauthorized, return null instead of throwing
        if (
          error &&
          typeof error === 'object' &&
          (('status' in error && error.status === 401) ||
            ('statusCode' in error && error.statusCode === 401))
        ) {
          return null;
        }
        throw error;
      }
    },
    // Only fetch when Firebase auth is ready and user is signed in
    enabled: !firebaseLoading && !!firebaseUser,
    staleTime: 60_000,
    retry: false,
  });
};
